import { store, useAppDispatch } from "@store";
import { updateStoryByKeys } from "@store/story";
import {
  getCurrentContentHash,
  isLoadedDocument,
} from "@store/story/selectors";
import type { WholeStoryType } from "@config";
import {
  addCollabBreadcrumb,
  captureSentryError,
} from "@/shared/utils/sentry-helpers";
import {
  collaborationApi,
  useUpdatePageMutation,
  type TPage,
} from "@/shared/services";
import { useUpdateVersionInDatabaseMutation } from "@api/versions";
import { toast } from "react-toastify";
import {
  PARK_RECONCILE_LIMIT,
  getLastKnownPointer,
  awaitLiveSyncConverged,
  hasReportedParkCap,
  isOwnPointer,
  isPeerPresent,
  markParkCapReported,
  noteParkedReconcile,
  resetParkedReconcile,
  revisionWriterId,
  runExclusive,
  setLastKnownPointer,
  setParkedReloadNotice,
  type PointerScope,
} from "@/features/collaboration/last-known-pointer";
import { useGeneratePreviewLink } from "./use-generate-preview-link";
import { useUploadMetadata } from "./use-upload-metadata";

// Sentinel distinguishing "the preview step parked" from a real previewUrl
// (always a string) or a generic failure (null). use-publish.tsx checks for
// this by identity to abort the publish instead of proceeding after a park —
// a plain `null` return doesn't carry enough information to tell "parked" apart
// from "render failed" / "discarded as stale-on-arrival", both of which are
// fine for the fire-and-forget autosave caller but NOT fine for publish.
export const PREVIEW_PARKED = Symbol("preview-parked");

// Collab-mode autosave. The FE owns the contentPointer here (the Yjs webhook
// flush stays as a backup) so both the Preview button and the next Publish see
// the latest edits. On every local autosave we:
//
//   1. Snapshot the current converged content hash (sourceHash) via the SAME
//      selector the freshness check uses, so the tag and the check always agree.
//   2. Render the preview HTML -> previewUrl. If the converged content moved on
//      DURING the render (current hash != sourceHash) and this is not a forced
//      (publish) run, DISCARD — a newer generation will produce the matching one.
//   3. Upload the converged snapshot -> a fresh contentPointer and persist
//      previewUrl + contentPointer + previewContentHash to the page/version.
//   4. Broadcast previewUrl + contentPointer + previewContentHash + updatedAt to
//      peers via the Yjs update-story path (NO broadcast:true). Whole-story
//      convergence: every field, including these derived pointers, must be
//      identical across users. updatedAt also lets the publish flow's change
//      detection (isPageChanged: updatedAt > publishedAt) recognize collab edits.
//
// Autosave fires on `changeId`, which only the LOCAL user's edits bump (remote
// changes arrive via syncFromYjs and don't), so there's no fan-out — the active
// editor records the snapshot, and because its Redux state is the converged
// document, that snapshot captures everyone's merged changes.
export const useUpdatePreviewOnly = () => {
  const { mutateAsync: updatePage } = useUpdatePageMutation();
  const [updateVersionInDatabase] = useUpdateVersionInDatabaseMutation();
  const handlePreviewLink = useGeneratePreviewLink();
  const handleUploadMetadata = useUploadMetadata();
  const dispatch = useAppDispatch();

  const runGeneration = async ({
    story,
    versionId,
    versionData,
    force = false,
  }: {
    story: WholeStoryType;
    versionId?: string;
    // The version's Handlebars data (merge-tag values). When present, the
    // preview HTML must be compiled with it so the reader sees resolved values
    // instead of raw `{{variable}}` templates — mirrors the non-collab
    // updatePreviewAndMeta path, which passes version?.data.
    versionData?: Record<string, unknown> | null;
    // Publish passes force=true: it is a foreground, authoritative action and
    // must always publish its rendered artifact, never discard on a late edit.
    force?: boolean;
  }) => {
    // Page and version saves are different pointer lineages on the backend
    // (see last-known-pointer.ts) — every read/write of the parent pointer in
    // this hook must go through this same scope.
    const scope: PointerScope = {
      storyId: story._id,
      versionId: versionId ?? null,
    };

    // Tag the artifacts with the SAME hash the freshness check
    // (useCollabHoldState) and the discard guard below use — getCurrentContentHash
    // over current Redux state. Hashing the passed `story` snapshot instead would
    // disagree with the consumer: the snapshot predates the Yjs reconcile that
    // re-derives Redux content (and round-trips settings through the Y.Map), so
    // storedHash would never equal currentHash, the discard would always fire,
    // and previewUrl/previewContentHash would never update (perpetually-stale
    // preview). One source of truth keeps tag, guard, and freshness consistent.
    const sourceHash = getCurrentContentHash(store.getState());

    const previewUrl = await handlePreviewLink(
      story,
      versionId,
      true,
      versionData,
    ).catch((error) => {
      console.error("[COLLAB] Preview render failed:", error);
      // Aborts the whole autosave generation → contentPointer/preview don't
      // advance this round. Repeated failures = the deck silently stops
      // saving, so this needs error-level visibility, not just a console line.
      captureSentryError(
        error instanceof Error ? error : new Error(String(error)),
        {
          tags: { feature: "collaboration", operation: "autosave-preview" },
          extra: { storyId: story._id, versionId: versionId ?? null },
          fingerprint: ["collab", "autosave-preview-failed"],
        },
      );
      return null;
    });
    if (!previewUrl) return null;

    // Conflict-free guard (autosave path only): if the converged content moved
    // on while we were rendering, this generation is stale-on-arrival — discard
    // it so it can't clobber a newer artifact. The edit that moved content has
    // its own autosave queued; that generation will publish the matching one.
    if (!force && getCurrentContentHash(store.getState()) !== sourceHash) {
      return null;
    }

    // Upload the converged snapshot to get a fresh contentPointer, then persist
    // it + previewUrl + the source hash so the live link / primary version build
    // from the latest content at publish time.
    let contentPointer: string | undefined;
    // Set only when record-revision succeeded AND promoted this revision to
    // head — that call already persisted settings.contentPointer atomically
    // under the backend's compare-and-swap gate. Gates whether the page
    // persist below repeats that field (see the comment at that call).
    let revisionRecorded = false;
    try {
      const meta = await handleUploadMetadata(
        Object.assign({ ...story }, { previewUrl }),
        versionId ?? null,
      );
      contentPointer = meta.url;

      // Record this snapshot as a revision in the shared session's history so
      // the version-history dialog ACCUMULATES collaborative revisions instead
      // of showing only the single pre-collab entry. The backend bumps the
      // `history.<sessionId>` entry atomically (revisions++, contributors
      // merged) and returns the fresh map. Best-effort: a failure here must not
      // break the autosave, and the backend flush (publish/last-leave/webhook)
      // is a backstop. `history` is local-only (stripped from the Yjs
      // broadcast), so we apply it with broadcast:true — updates this client's
      // dialog without echoing into the shared doc.
      // Narrowed to the network call only: everything that reacts to the
      // result (the history dispatch, the parked branch, the reload) lives in
      // the `if (revision)` block below, OUTSIDE this try. If any of THAT
      // throws (a reducer bug, `reload()` rejecting in a sandboxed context),
      // it must propagate to the OUTER catch (below, "Updating preview
      // metadata failed") — which skips the persist — rather than landing
      // here with `revisionRecorded` left false and falling through to a
      // persist that would write the stale pointer via the CAS-free
      // updatePage route. Fail-closed, not fail-open.
      // Telemetry only — never used to derive the parent (that would make the
      // guard vacuous, see the module doc comment). In the real incident the
      // uploaded blob carried its stale parent INSIDE it, so this disagreement
      // is the one signal that would have fired on the offending autosave.
      const scopedParent = getLastKnownPointer(scope);
      const reduxContentPointer = story.settings?.contentPointer ?? null;
      if (reduxContentPointer !== scopedParent) {
        addCollabBreadcrumb(
          "contentPointer mismatch: redux vs lastKnownPointer",
          {
            storyId: story._id,
            versionId: versionId ?? null,
            reduxContentPointer,
            lastKnownPointer: scopedParent,
          },
        );
      }

      const record = async (parentPointer: string | null) => {
        try {
          return await collaborationApi.recordRevision({
            storyId: story._id,
            versionId: versionId ?? null,
            url: contentPointer as string,
            sessionId: meta.sessionId,
            userId: meta.userId,
            previewUrl,
            parentPointer,
          });
        } catch (revErr) {
          console.error("[COLLAB] record-revision failed:", revErr);
          // Best-effort (updatePage below is the backstop), so a breadcrumb —
          // not a standalone error — but it's part of the pointer/history trail
          // if a later error is captured this session.
          addCollabBreadcrumb("record-revision failed", {
            storyId: story._id,
            versionId: versionId ?? null,
            message: revErr instanceof Error ? revErr.message : String(revErr),
          });
          return null;
        }
      };

      let revision = await record(scopedParent);

      // Self-park recovery. The head that beat us is a pointer THIS browsing
      // context uploaded, so no co-editor is involved — we lost a race with
      // ourselves and our parent was simply read too early. `runExclusive`
      // should now make that unreachable; this is the second line of defence
      // for the paths that still write the head outside a generation (the
      // updatePage/updateVersion backstop below, and the legacy
      // updatePreviewAndMeta autosave).
      //
      // Re-record the SAME uploaded snapshot against the corrected parent
      // rather than dropping it: our blob was uploaded later than the head, so
      // it carries the newer content, and the backend's timestamp gate accepts
      // it. Once only — a second park here is a real conflict and falls through
      // to the handling below.
      if (
        revision &&
        revision.promoted === false &&
        revision.contentPointer &&
        isOwnPointer(scope, revision.contentPointer)
      ) {
        addCollabBreadcrumb("self-park — re-recording against own head", {
          storyId: story._id,
          versionId: versionId ?? null,
          parent: scopedParent,
          head: revision.contentPointer,
          incoming: contentPointer,
        });
        const retried = await record(revision.contentPointer);
        if (retried) revision = retried;
      }

      if (revision) {
        const { history, contentPointer: resolvedPointer, promoted } = revision;

        // Only into the document this save is for: after a template <->
        // version navigation the store may already hold the other document.
        if (history && isLoadedDocument(store.getState(), scope)) {
          dispatch(
            updateStoryByKeys({
              data: { history },
              autosave: false,
              broadcast: true,
            }),
          );
        }

        // Parked: this client's content descends from a pointer that is no
        // longer the head, so the backend recorded the revision without
        // promoting it.
        //
        // Two escape hatches that tried to distinguish "legitimate co-editor
        // park" from real staleness were tried here and removed:
        //   - "own write" matched the returned head's sessionId against
        //     meta.sessionId, but sessionId is the room's SHARED collab session
        //     (CurrentSessionProvider mirrors the Liveblocks `session` into the
        //     cookie use-upload-metadata.tsx reads), so every peer and every tab
        //     in the story carries the same one — prod logs show three users
        //     under one sessionId. A stale tab could adopt anyone's head as if
        //     it were "its own write".
        //   - "converged" (commit ec5e0d180, reverted) trusted isConverged(),
        //     which is just `connected && !hasUnsyncedChanges` with no peer
        //     check at all — a lone stale client reads that as true, so it
        //     fired on exactly the original re-seeding incident and turned a
        //     park into a one-cycle-delayed overwrite instead of preventing it.
        // What DOES work is the own-pointer registry consulted above: instead
        // of inferring authorship from a shared cookie or from convergence, it
        // records the pointers this browsing context actually uploaded, which
        // no other tab can contain. That resolves the self-race case without
        // peer awareness.
        //
        // A legitimate CO-EDITOR park is recognised through real peer
        // awareness: YjsStoryProvider publishes the room's present user ids
        // (setPresentPeerIds), and the head pointer names its writer. When that
        // writer is in the room right now AND our transport is converged
        // (connected, no unacknowledged local updates — isLiveSyncConverged),
        // both tabs hold the same shared Y.Doc, so our edits are already in the
        // content they autosave from — we defer to their head, never reload. A
        // present peer with a lagging socket is NOT that case: our local copy
        // could be behind theirs and the next autosave would promote it over
        // their edits, so it falls through to the reload path with everything
        // else. Any other park is treated as staleness.
        if (promoted === false) {
          const headWriter = revisionWriterId(resolvedPointer);
          const peerHoldsHead =
            Boolean(resolvedPointer) && isPeerPresent(headWriter);
          // Own updates await the server's ack while the user types, so give
          // convergence a short window rather than deciding on one instant.
          const converged = peerHoldsHead && (await awaitLiveSyncConverged());
          if (!converged) {
            // Says WHY this park is about to take the reload path below:
            // nobody present wrote the head, or the transport never converged.
            addCollabBreadcrumb(
              peerHoldsHead
                ? "revision parked by a live co-editor but transport not converged — reloading"
                : "revision parked — head writer not present in room",
              {
                storyId: story._id,
                versionId: versionId ?? null,
                head: resolvedPointer,
                headWriter,
              },
            );
          }
          if (converged) {
            // Adopt their head as the parent of our NEXT autosave (3s away),
            // which then promotes normally and carries these edits. Reloading
            // here instead re-seeds the tab from the durable head and drops
            // everything typed since it — with two people in a deck that fired
            // on every peer promote, and after PARK_RECONCILE_LIMIT the tab
            // stopped saving altogether (Winter Circle, Monday #13070251938:
            // 52 of 222 saves lost). Not a park for the cap's purposes either.
            addCollabBreadcrumb(
              "revision parked by a live co-editor — adopting their head",
              {
                storyId: story._id,
                versionId: versionId ?? null,
                parent: scopedParent,
                head: resolvedPointer,
                headWriter,
              },
            );
            setLastKnownPointer(scope, resolvedPointer);
            resetParkedReconcile(scope);
            if (force) {
              // Publish stays a foreground, fail-visibly action (see below), but
              // the parent is now current, so a second click succeeds without
              // the reload the generic message asks for.
              toast.error(
                "Couldn't publish — a collaborator saved newer changes moments ago. Please try again.",
              );
              return PREVIEW_PARKED;
            }
            return null;
          }

          const attempts = noteParkedReconcile(scope);
          addCollabBreadcrumb("revision parked (stale parent)", {
            storyId: story._id,
            versionId: versionId ?? null,
            parent: scopedParent,
            head: resolvedPointer,
            attempts,
          });

          if (force) {
            // Publish is a foreground, authoritative action (see the force
            // comment at the top of this hook). Reloading out from under an
            // explicit Publish click destroys the user's action without consent
            // and can interrupt the multi-step publish flow — and publishing
            // content we were just told is stale would be worse still. Report
            // and stop; the publish flow fails cleanly and visibly. Return the
            // PARKED sentinel (not previewUrl/null) so use-publish can tell
            // this apart from every other failure/discard path and abort the
            // publish instead of proceeding to save-and-publish anyway.
            toast.error(
              "Couldn't publish — newer changes were saved elsewhere. Reload the page and try again.",
            );
            return PREVIEW_PARKED;
          }

          if (attempts >= PARK_RECONCILE_LIMIT) {
            // Report the cap exactly once per scope — without this guard, every
            // further autosave (the user keeps typing, the doc keeps parking)
            // would re-fire a Sentry event AND an error toast on each cycle.
            if (!hasReportedParkCap(scope)) {
              captureSentryError(
                new Error("Autosave parked repeatedly — stale parent pointer"),
                {
                  tags: {
                    feature: "collaboration",
                    operation: "autosave-park",
                  },
                  extra: {
                    storyId: story._id,
                    versionId: versionId ?? null,
                    attempts,
                    head: resolvedPointer,
                  },
                  fingerprint: ["collab", "autosave-parked-repeatedly"],
                },
              );
              toast.error(
                "Your changes aren't saving. Please reload the page before editing further.",
              );
              markParkCapReported(scope);
            }
            return null;
          }

          // Flag first, THEN reload — a toast shown here would be destroyed by
          // the reload and the user would see an unexplained refresh. Not
          // adopting `resolvedPointer` into lastKnownPointer here: the reload
          // tears down module state before anything could read it, and
          // `setupStore` re-derives the pointer from the fresh fetch after
          // reload.
          setParkedReloadNotice();
          window.location.reload();
          return null;
        }

        // Promoted. Adopt the backend's resolved pointer as the parent of
        // the next edit, and clear the park counter.
        revisionRecorded = true;
        if (resolvedPointer) contentPointer = resolvedPointer;
        setLastKnownPointer(scope, resolvedPointer ?? contentPointer);
        resetParkedReconcile(scope);
      }

      // The parent for the follow-up persist is whatever the head is NOW — after
      // a successful promote that is the pointer we just promoted, not the one
      // we descended from. Reusing `scopedParent` here made the version route's
      // CAS log a spurious "revision PARKED" and drop an already-correct write
      // on EVERY successful version autosave (seen in prod on 2026-08-27,
      // version 6a902ee21ac5d9c8c56cb916, where incoming === head).
      const persistParent = getLastKnownPointer(scope);

      if (versionId) {
        // Persist version.data alongside the preview metadata so dynamic-
        // variable values (in-editor popover) survive a full reload even when
        // the user never opens Share — mirrors the non-collab autosave, which
        // always writes version.data. version.data is synced live via Yjs but
        // is NOT part of the Yjs→GCS content flush, so the DB version record is
        // its only durable home.
        const versionData = store.getState().story.version?.data;
        const updatedVersion = await updateVersionInDatabase({
          versionId,
          payload: {
            previewUrl,
            contentPointer,
            previewContentHash: sourceHash,
            // Control field, not persisted: lets the backend's CAS gate drop a
            // stale contentPointer instead of writing it when this pointer no
            // longer matches version.contentPointer's stored head.
            parentPointer: persistParent,
            ...(versionData ? { data: versionData } : {}),
          },
        }).unwrap();
        // Adopt the head the server actually holds. When its CAS dropped our
        // pointer this is the winning one, so the NEXT generation descends from
        // the truth instead of re-parking against a head we never learned about.
        if (updatedVersion?.contentPointer) {
          setLastKnownPointer(scope, updatedVersion.contentPointer);
        } else {
          // The route echoes the stored version, and we always send a
          // contentPointer, so an absent one means the response shape changed
          // under us — the next generation then descends from a parent nothing
          // confirmed. A breadcrumb, NOT a throw: the save itself succeeded,
          // and failing it here would report a healthy autosave as broken.
          addCollabBreadcrumb("persist returned no contentPointer", {
            storyId: story._id,
            versionId,
            sent: contentPointer,
            parentPointer: persistParent,
          });
        }
      } else {
        // Dotted paths, NOT a `settings` spread: sending the whole subdocument
        // replaces it, which lets a stale client revert every other settings
        // key on each autosave (Monday #12732695541). The backend allowlists
        // exactly these two dotted keys.
        //
        // settings.contentPointer is included ONLY as a backstop, when
        // record-revision never persisted it (revisionRecorded is false — the
        // call threw, or never ran). When record-revision DID succeed and
        // promote, it already wrote settings.contentPointer atomically under
        // the backend's compare-and-swap gate; POST /pages/v2/:id (what
        // updatePage calls) has no staleness check at all, so writing this
        // field here unconditionally would reopen a CAS-free path to the
        // exact field this branch exists to protect — a slow/retried autosave
        // could silently roll back a peer's newer pointer.
        const updatedPage = await updatePage({
          id: story._id,
          data: {
            previewUrl,
            "settings.previewContentHash": sourceHash,
            // Backstop only (record-revision threw / never ran): send
            // parentPointer alongside so the backend's CAS gate on
            // POST /pages/v2/:id can drop this stale contentPointer instead of
            // writing it, rather than writing it via this CAS-free route
            // unconditionally.
            ...(revisionRecorded
              ? {}
              : {
                  "settings.contentPointer": contentPointer,
                  parentPointer: persistParent,
                }),
          } as unknown as Partial<TPage>,
          skipInvalidation: true,
        });
        // Adopt the head the server actually holds — see the version branch.
        // This is what closes the backstop path: it advances the head via a
        // route that never calls setLastKnownPointer, so before this every
        // successful backstop write left the parent stale and parked the NEXT
        // autosave.
        const persistedPointer = updatedPage?.settings?.contentPointer;
        if (persistedPointer) {
          setLastKnownPointer(scope, persistedPointer);
        } else if (!revisionRecorded) {
          // Only anomalous on the backstop path. When record-revision promoted,
          // we deliberately send no pointer here and the local head is already
          // correct, so an absent one is expected. When it did NOT, we sent one
          // and the response should carry either it or the head that beat it —
          // absent means neither, and the next generation descends from a
          // parent nothing confirmed. Breadcrumb, not a throw: see the version
          // branch.
          addCollabBreadcrumb("persist returned no contentPointer", {
            storyId: story._id,
            versionId: null,
            sent: contentPointer,
            parentPointer: persistParent,
          });
        }
      }
    } catch (error) {
      console.error("[COLLAB] Updating preview metadata failed:", error);
      // The snapshot upload / pointer persist failed → this round's edits live
      // only in the Y.Doc until the next successful autosave. Error-level so we
      // see persistence failures, which are the heart of the data-loss reports.
      captureSentryError(
        error instanceof Error ? error : new Error(String(error)),
        {
          tags: { feature: "collaboration", operation: "autosave-persist" },
          extra: { storyId: story._id, versionId: versionId ?? null },
          fingerprint: ["collab", "autosave-persist-failed"],
        },
      );
    }

    // Apply the derived artifacts to LOCAL Redux only — broadcast:true skips the
    // Yjs update-story path. These are derived re-assertions of the WHOLE story;
    // broadcasting them re-asserts every setting (title, etc.) on each autosave,
    // which (a) created a spurious undo step per save and (b) — as the last
    // writer — shadowed the user's own settings edits so undo couldn't revert
    // them. Real settings edits broadcast (and converge + are undoable) through
    // their OWN updateStoryByKeys dispatch, so peers still get those. The derived
    // keys themselves are persisted durably via record-revision/updatePage (DB);
    // peers pick them up on publish/reload rather than via the live doc.
    // (Monday #12337353624)
    if (isLoadedDocument(store.getState(), scope))
      dispatch(
        updateStoryByKeys({
          data: {
            previewUrl,
            updatedAt: new Date().toISOString(),
            settings: {
              ...(contentPointer ? { contentPointer } : {}),
              previewContentHash: sourceHash,
            },
          },
          broadcast: true,
        }),
      );

    return previewUrl;
  };

  // Generations for one scope run ONE AT A TIME. Each reads the parent pointer
  // and promotes at opposite ends of a multi-second upload, so overlapping
  // generations both read the same parent, the first promotes, and the second
  // parks against a head it never saw — which the park path then treats as a
  // co-editor conflict and reloads the tab. See runExclusive's doc comment for
  // the production trace.
  //
  // This also serialises publish (force: true, called straight from use-publish
  // outside the autosave debounce) against a concurrent autosave: publish now
  // waits for the in-flight generation and reads the parent it left behind,
  // instead of racing it.
  return (args: Parameters<typeof runGeneration>[0]) =>
    runExclusive(
      { storyId: args.story._id, versionId: args.versionId ?? null },
      () => runGeneration(args),
    );
};
